import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, errorText, mediaUrl } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { ImageField, UploadButton } from "./MediaUpload";

const CATEGORIES = ["Makeup", "Beauty", "Skincare", "Jewellery"];
const LABELS = ["Creative Concept", "Personal Project", "Brand Collaboration"];
const FORMATS = ["Product Photography", "Video Ads", "Product Films"];
const BLANK = { title: "", brand: "", category: "Makeup", formats: [], label: "Creative Concept", year: String(new Date().getFullYear()), orientation: "portrait", description: "", cover_image: "", images: [], video_url: "", external_link: "", featured: false, visible: true };

export const L = ({ children }) => <p className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-charcoal/80">{children}</p>;
export const Sel = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full border border-input bg-transparent px-3 text-sm">
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const ProjectForm = ({ open, project, onClose, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(project ? { ...BLANK, ...project } : BLANK); }, [project, open]);
  const set = (k) => (val) => setF((s) => ({ ...s, [k]: val }));
  const toggleFormat = (fm) => set("formats")(f.formats.includes(fm) ? f.formats.filter((x) => x !== fm) : [...f.formats, fm]);

  const save = async () => {
    if (!f.title.trim()) return toast.error("Please add a title.");
    if (!f.cover_image) return toast.error("Please upload a cover image.");
    setSaving(true);
    try {
      const { id, slug, order, ...body } = f;
      if (project?.id) await api.put(`/admin/projects/${project.id}`, { ...body, slug });
      else await api.post("/admin/projects", body);
      toast.success("Project saved");
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-ivory" data-testid="project-form-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal text-wine">{project?.id ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2"><L>Title *</L><Input value={f.title} onChange={(e) => set("title")(e.target.value)} data-testid="pf-title" /></div>
          <div><L>Brand name</L><Input value={f.brand} onChange={(e) => set("brand")(e.target.value)} placeholder="e.g. BWI Concept" data-testid="pf-brand" /></div>
          <div><L>Year</L><Input value={f.year} onChange={(e) => set("year")(e.target.value)} data-testid="pf-year" /></div>
          <div><L>Category</L><Sel value={f.category} onChange={set("category")} options={CATEGORIES} testid="pf-category" /></div>
          <div><L>Project label</L><Sel value={f.label} onChange={set("label")} options={LABELS} testid="pf-label" /></div>
          <div><L>Main orientation</L><Sel value={f.orientation} onChange={set("orientation")} options={["portrait", "landscape", "square"]} testid="pf-orientation" /></div>
          <div>
            <L>Formats</L>
            <div className="flex flex-wrap gap-2">
              {FORMATS.map((fm) => (
                <button key={fm} type="button" onClick={() => toggleFormat(fm)} data-testid={`pf-format-${fm.toLowerCase().replace(/\s/g, "-")}`} className={`border px-2.5 py-1 text-xs ${f.formats.includes(fm) ? "border-wine bg-wine text-ivory" : "border-line text-charcoal"}`}>{fm}</button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2"><L>Short description</L><Textarea rows={4} value={f.description} onChange={(e) => set("description")(e.target.value)} data-testid="pf-description" /></div>
          <div className="sm:col-span-2"><ImageField label="Cover image *" value={f.cover_image} onChange={set("cover_image")} testid="pf-cover-upload" /></div>
          <div className="sm:col-span-2">
            <L>Gallery images</L>
            <div className="flex flex-wrap gap-3">
              {f.images.map((u) => (
                <div key={u} className="relative h-20 w-16 border border-line">
                  <img src={mediaUrl(u)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => set("images")(f.images.filter((x) => x !== u))} className="absolute -right-2 -top-2 rounded-full bg-wine p-0.5 text-ivory" aria-label="Remove image"><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="mt-3"><UploadButton multiple label="Add images" onUploaded={(u) => setF((s) => ({ ...s, images: [...s.images, u] }))} testid="pf-gallery-upload" /></div>
          </div>
          <div className="sm:col-span-2">
            <L>Video — upload a file (MP4/WEBM/MOV, up to 100 MB) or paste a YouTube / Vimeo link</L>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Input value={f.video_url} onChange={(e) => set("video_url")(e.target.value)} placeholder="https://youtube.com/…" data-testid="pf-video-url" />
              <UploadButton accept="video/mp4,video/webm,video/quicktime" label="Upload video" onUploaded={set("video_url")} testid="pf-video-upload" />
            </div>
          </div>
          <div className="sm:col-span-2"><L>External link (optional)</L><Input value={f.external_link} onChange={(e) => set("external_link")(e.target.value)} placeholder="https://" data-testid="pf-external-link" /></div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.visible} onCheckedChange={set("visible")} data-testid="pf-visible" /> Visible on website</label>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.featured} onCheckedChange={set("featured")} data-testid="pf-featured" /> Feature on Home</label>
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary" data-testid="pf-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn btn-primary disabled:opacity-60" data-testid="pf-save">{saving ? "Saving…" : "Save project"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
